import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Predictor code to display name mapping
const PREDICTOR_NAME_MAP = {
    'DKQ': 'MMA Guru',
    'kpg': 'LucasTracy',
    "lZg": "Bedtime",
    "Q9w": "Fightfairy",
    "0ZQ": "MMA JOEY",
    'favorites': 'Favorites',
    'underdogs': 'Underdogs'
};

function Predictors() {
    const [eventSummaries, setEventSummaries] = useState([]);
    const [expandedEvent, setExpandedEvent] = useState(null);
    const [expandedEventFights, setExpandedEventFights] = useState([]);
    const [loadingFights, setLoadingFights] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [predictorNames, setPredictorNames] = useState([]);
    const [predictorCodes, setPredictorCodes] = useState([]);
    const [showProfit, setShowProfit] = useState(false);

    useEffect(() => {
        fetchEventSummaries();
    }, []);

    const fetchEventSummaries = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/api/predictors/summary`);
            if (!res.ok) throw new Error('Failed to fetch predictors summary');
            const data = await res.json();
            setEventSummaries(data.events);
            // Sort predictors: real predictors first, then hypothetical ones (favorites, underdogs)
            const sortedPredictors = data.predictors.sort((a, b) => {
                const hypothetical = ['favorites', 'underdogs'];
                const aIsHypothetical = hypothetical.includes(a);
                const bIsHypothetical = hypothetical.includes(b);
                if (aIsHypothetical !== bIsHypothetical) {
                    return aIsHypothetical ? 1 : -1;
                }
                return 0;
            });
            setPredictorCodes(sortedPredictors);
        } catch (e) {
            console.error('Error fetching predictors summary:', e);
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchEventFights = async (eventName) => {
        setLoadingFights(true);
        try {
            const res = await fetch(`${API_BASE}/api/predictors/event-fights?event_name=${encodeURIComponent(eventName)}`);
            if (!res.ok) throw new Error('Failed to fetch event fights');
            const data = await res.json();
            setExpandedEventFights(data);
        } catch (e) {
            console.error('Error fetching event fights:', e);
        } finally {
            setLoadingFights(false);
        }
    };

    const handleEventClick = (eventName) => {
        if (expandedEvent === eventName) {
            setExpandedEvent(null);
            setExpandedEventFights([]);
        } else {
            setExpandedEvent(eventName);
            fetchEventFights(eventName);
        }
    };

    const containerStyle = {
        maxWidth: '1150px',
        margin: '0 auto',
        padding: '2rem',
        color: '#aaa'
    };

    const titleStyle = {
        fontSize: '1.75rem',
        fontWeight: 600,
        marginBottom: '0.5rem',
        textAlign: 'center',
        color: '#fff'
    };

    const tableStyle = {
        width: 'auto',
        borderCollapse: 'collapse',
        backgroundColor: '#232323', // changed from blue
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        marginBottom: '2rem',
        tableLayout: 'auto',
        border: '1px solid #444' // changed from blue
    };

    const thStyle = {
        backgroundColor: '#2c2c2c',
        color: '#fff', // header text is now white
        padding: '0.6rem 0.8rem',
        textAlign: 'left',
        fontWeight: 500,
        borderBottom: '1px solid #444',
        whiteSpace: 'nowrap'
    };

    const tdStyle = {
        padding: '0.6rem 0.8rem',
        borderBottom: '1px solid #444',
        textAlign: 'left',
        whiteSpace: 'nowrap',
        color: '#aaa' // use light gray for all cell text
    };

    const accuracyCellStyle = {
        padding: '0.6rem 0.8rem',
        borderBottom: '1px solid #444', // changed from blue
        textAlign: 'center',
        whiteSpace: 'nowrap'
    };

    const rowStyle = (index) => ({
        ...tdStyle,
        backgroundColor: '#232323', // all rows same color
        cursor: 'pointer',
        transition: 'background-color 0.2s ease'
    });

    const getAccuracyColor = (accuracy) => {
        // Red to White to Green gradient for accuracy (0% to 100%) - 60% is neutral/okay
        if (accuracy < 0.33) return 'rgba(192, 57, 43, 0.2)'; // Dark Red
        if (accuracy < 0.45) return 'rgba(231, 76, 60, 0.2)'; // Red
        if (accuracy < 0.55) return 'rgba(236, 112, 99, 0.2)'; // Light Red
        if (accuracy < 0.65) return 'rgba(236, 240, 241, 0.2)'; // Light Gray (neutral around 60%)
        if (accuracy < 0.75) return 'rgba(169, 223, 191, 0.2)'; // Light Green
        if (accuracy < 0.85) return 'rgba(82, 190, 128, 0.2)'; // Medium Green
        if (accuracy < 0.95) return 'rgba(60, 179, 113, 0.2)'; // Medium Sea Green
        return 'rgba(39, 174, 96, 0.2)'; // Dark Green
    };

    const getProfitColor = (profit) => {
        // Red to White to Green gradient for profit, with neutral between -10% and 0%
        if (profit < -0.5) return 'rgba(192, 57, 43, 0.2)'; // Dark Red
        if (profit < -0.2) return 'rgba(231, 76, 60, 0.2)'; // Red
        if (profit < -0.1) return 'rgba(236, 112, 99, 0.2)'; // Light Red
        if (profit < 0) return 'rgba(236, 240, 241, 0.2)'; // Light Gray (neutral -10% to 0%)
        if (profit < 0.1) return 'rgba(169, 223, 191, 0.2)'; // Light Green
        if (profit < 0.2) return 'rgba(82, 190, 128, 0.2)'; // Medium Green
        return 'rgba(39, 174, 96, 0.2)'; // Dark Green
    };

    const accuracyTextStyle = (accuracy) => ({
        fontSize: '0.95rem',
        fontWeight: 'normal',
        color: '#aaa' // use light gray for all cell text
    });

    if (loading) {
        return (
            <div style={containerStyle}>
                <div style={titleStyle}>Predictors Performance</div>
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={containerStyle}>
                <div style={titleStyle}>Predictors Performance</div>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#e74c3c' }}>
                    Error: {error}
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <div style={titleStyle}>UFC Predictors</div>
            <div style={{ textAlign: 'center', marginBottom: '6rem', color: '#aaa', fontSize: '0.95rem' }}>
                Automated LLM-based extraction of UFC card predictions by popular MMA Youtube channels from text-to-speech transcripts. Not updated anymore - demo purposes only.
            </div>
            <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', width: '90%', position: 'relative' }}>
                <p style={{ color: '#aaa', margin: 0, fontSize: '0.75rem', position: 'absolute', left: '25%', transform: 'translateX(-50%)' }}>
                    Click on any event to view detailed fight predictions
                </p>
                <div 
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        cursor: 'pointer',
                        userSelect: 'none',
                        opacity: !eventSummaries.some(event => Object.values(event.has_odds_data || {}).some(v => v)) ? 0.4 : 1,
                        pointerEvents: !eventSummaries.some(event => Object.values(event.has_odds_data || {}).some(v => v)) ? 'none' : 'auto'
                    }}
                >
                    <span style={{
                        fontSize: '0.9rem',
                        color: '#aaa',
                        fontWeight: 400
                    }}>
                        Accuracy
                    </span>
                    <div style={{
                        width: '32px',
                        height: '20px',
                        background: '#444',
                        borderRadius: '10px',
                        position: 'relative',
                        transition: 'all 0.25s ease',
                        border: '1px solid #555',
                        cursor: 'pointer'
                    }}
                    onClick={() => setShowProfit(!showProfit)}
                    >
                        <div style={{
                            width: '16px',
                            height: '16px',
                            background: '#fff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: showProfit ? '14px' : '2px',
                            transition: 'all 0.25s ease'
                        }} />
                    </div>
                    <span style={{
                        fontSize: '0.9rem',
                        color: '#aaa',
                        fontWeight: 400
                    }}>
                        Profit
                    </span>
                </div>
            </div>

            {eventSummaries.length > 0 ? (
                <div style={{ overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Event</th>
                                <th style={thStyle}>Date</th>
                                {predictorCodes.map((code) => (
                                    <th key={code} style={thStyle}>
                                        {PREDICTOR_NAME_MAP[code] || code}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {eventSummaries.filter(event => {
                                // Only show rows that have at least one real predictor (not just favorites/underdogs)
                                const hypotheticalPredictors = ['favorites', 'underdogs'];
                                const hasRealPredictorData = predictorCodes.some(code => {
                                    if (hypotheticalPredictors.includes(code)) return false;
                                    const accuracy = event.accuracies[code];
                                    return accuracy !== undefined && accuracy !== null;
                                });
                                return hasRealPredictorData;
                            }).map((event, index) => (
                                <React.Fragment key={event.event_name}>
                                    <tr
                                        onClick={() => handleEventClick(event.event_name)}
                                        style={{
                                            ...rowStyle(index),
                                            borderBottom: expandedEvent === event.event_name ? '1px solid #888' : undefined
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#333'; // highlight on hover
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#232323'; // revert to same color
                                        }}
                                    >
                                        <td style={tdStyle}>{event.event_name}</td>
                                        <td style={tdStyle}>{event.event_date || 'N/A'}</td>
                                        {predictorCodes.map((code) => {
                                            const accuracy = event.accuracies[code];
                                            const profit = event.profits[code];
                                            const hasOdds = event.has_odds_data && event.has_odds_data[code];
                                            const displayValue = showProfit ? profit : accuracy;
                                            const shouldDisplay = showProfit ? hasOdds : true;
                                            const link = event.links && event.links[code];
                                            
                                            // Determine background color based on gradient
                                            let cellBackgroundColor = 'transparent';
                                            if (shouldDisplay && displayValue !== undefined && displayValue !== null) {
                                                if (showProfit) {
                                                    cellBackgroundColor = getProfitColor(displayValue);
                                                } else {
                                                    cellBackgroundColor = getAccuracyColor(displayValue);
                                                }
                                            }
                                            
                                            const dynamicCellStyle = {
                                                ...accuracyCellStyle,
                                                backgroundColor: cellBackgroundColor
                                            };
                                            
                                            return (
                                                <td key={code} style={dynamicCellStyle}>
                                                    {shouldDisplay && displayValue !== undefined && displayValue !== null ? (
                                                        link ? (
                                                            <a
                                                                href={link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{
                                                                    ...accuracyTextStyle(displayValue),
                                                                    textDecoration: 'none',
                                                                    cursor: 'pointer',
                                                                    color: '#aaa'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.color = '#fff';
                                                                    e.currentTarget.style.textDecoration = 'underline';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.color = '#aaa';
                                                                    e.currentTarget.style.textDecoration = 'none';
                                                                }}
                                                            >
                                                                {Math.round(displayValue * 100)}%
                                                            </a>
                                                        ) : (
                                                            <span style={accuracyTextStyle(displayValue)}>
                                                                {Math.round(displayValue * 100)}%
                                                            </span>
                                                        )
                                                    ) : (
                                                        ''
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {expandedEvent === event.event_name && (
                                        <tr>
                                            <td colSpan={2 + predictorCodes.length} style={{ background: '#2a2a2a', padding: '1.5rem' }}>
                                                {loadingFights ? (
                                                    <div>Loading fights...</div>
                                                ) : expandedEventFights.length > 0 ? (
                                                    <div style={{ overflowX: 'auto' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                                                            <thead>
                                                                <tr style={{ backgroundColor: '#2c2c2c', borderBottom: '1px solid #444' }}>
                                                                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 500, color: '#aaa' }}>Fighter 1</th>
                                                                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 500, color: '#aaa' }}>Fighter 2</th>
                                                                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 500, color: '#aaa' }}>Winner</th>
                                                                    {predictorCodes.map((code) => (
                                                                        <th key={code} style={{ padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 500, color: '#aaa' }}>
                                                                            {(PREDICTOR_NAME_MAP[code] || code)}
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {expandedEventFights.map((fight, fightIndex) => (
                                                                    <tr
                                                                        key={fightIndex}
                                                                        style={{
                                                                            backgroundColor: '#232323', // match main table
                                                                            borderBottom: '1px solid #444'
                                                                        }}
                                                                    >
                                                                        <td style={{ padding: '0.4rem 0.5rem', color: '#aaa' }}>{fight.fighter1}</td>
                                                                        <td style={{ padding: '0.4rem 0.5rem', color: '#aaa' }}>{fight.fighter2}</td>
                                                                        <td style={{ padding: '0.4rem 0.5rem', color: '#aaa' }}>
                                                                            {fight.winner}
                                                                        </td>
                                                                        {predictorCodes.map((code) => {
                                                                            const prediction = fight.predictions[code];
                                                                            const profitValue = fight.profits[code];
                                                                            let circleColor = '#888';
                                                                            const hasOdds = fight.fighter1_odds !== null && fight.fighter1_odds !== undefined && fight.fighter2_odds !== null && fight.fighter2_odds !== undefined;
                                                                            const isNC = fight.winner === 'NC';
                                                                            
                                                                            if (prediction && hasOdds && !isNC) {
                                                                                if (showProfit) {
                                                                                    circleColor = profitValue > 0 ? '#27ae60' : profitValue < 0 ? '#e74c3c' : '#888';
                                                                                } else {
                                                                                    const isCorrect = prediction === fight.winner;
                                                                                    circleColor = isCorrect ? '#27ae60' : '#e74c3c';
                                                                                }
                                                                            }
                                                                            return (
                                                                                <td
                                                                                    key={code}
                                                                                    style={{
                                                                                        padding: '0.4rem 0.5rem',
                                                                                        textAlign: 'center'
                                                                                    }}
                                                                                >
                                                                                    {prediction && hasOdds ? (
                                                                                        showProfit ? (
                                                                                            <span style={{ 
                                                                                                color: profitValue > 0 ? '#27ae60' : profitValue < 0 ? '#e74c3c' : '#f1f1f1', 
                                                                                                fontSize: '0.85rem',
                                                                                                fontWeight: 'bold'
                                                                                            }}>
                                                                                                {profitValue > 0 ? '+' : ''}{(profitValue * 100).toFixed(0)}
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span
                                                                                                style={{
                                                                                                    display: 'inline-block',
                                                                                                    width: '12px',
                                                                                                    height: '12px',
                                                                                                    borderRadius: '50%',
                                                                                                    backgroundColor: circleColor
                                                                                                }}
                                                                                            />
                                                                                        )
                                                                                    ) : prediction ? (
                                                                                        <span
                                                                                            style={{
                                                                                                display: 'inline-block',
                                                                                                width: '12px',
                                                                                                height: '12px',
                                                                                                borderRadius: '50%',
                                                                                                backgroundColor: '#888'
                                                                                            }}
                                                                                        />
                                                                                    ) : (
                                                                                        '—'
                                                                                    )}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div>No fights found for this event.</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {/* Average Row */}
                            <tr style={{
                                backgroundColor: '#232323', // match all rows, remove blue
                                fontWeight: 500,
                                borderTop: '1px solid #888'
                            }}>
                                <td style={tdStyle}>Average</td>
                                <td style={tdStyle}>—</td>
                                {predictorCodes.map((code) => {
                                    const values = eventSummaries
                                        .map(event => showProfit ? event.profits[code] : event.accuracies[code])
                                        .filter(v => v !== undefined && v !== null);
                                    
                                    const average = values.length > 0 
                                        ? values.reduce((sum, v) => sum + v, 0) / values.length 
                                        : null;
                                    
                                    let cellBackgroundColor = 'transparent';
                                    if (average !== null) {
                                        if (showProfit) {
                                            cellBackgroundColor = getProfitColor(average);
                                        } else {
                                            cellBackgroundColor = getAccuracyColor(average);
                                        }
                                    }
                                    
                                    return (
                                        <td
                                            key={code}
                                            style={{
                                                ...accuracyCellStyle,
                                                backgroundColor: cellBackgroundColor,
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {average !== null ? (
                                                <span style={accuracyTextStyle(average)}>
                                                    {Math.round(average * 100)}%
                                                </span>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
                    No events found.
                </div>
            )}
        </div>
    );
}

export default Predictors;
